// components/Dashboard.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { 
  Users, Package, DollarSign, Bell, LogOut, UserCog, 
  AlertTriangle, Clock, CheckCircle, ArrowRight, TrendingUp, 
  Calendar, User, Home, Phone 
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useCustomers } from '../hooks/useCustomers';
import { useDeliveries } from '../hooks/useDeliveries';
import { usePayments } from '../hooks/usePayments';
import { useReminders } from '../hooks/useReminders';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { customers } = useCustomers();
  const { deliveries } = useDeliveries();
  const { payments } = usePayments();
  const { reminders, summary, loading } = useReminders();

  const [stats, setStats] = useState({
    customers: 0,
    deliveries: 0,
    payments: 0,
    totalPending: 0,
    totalCollected: 0
  });

  useEffect(() => {
    // Calculate stats
    const totalPayments = payments || [];
    const totalPaid = totalPayments.reduce((sum, p) => sum + (p.amount_paid || 0), 0);
    const totalAmount = totalPayments.reduce((sum, p) => sum + (p.total_amount || 0), 0);
    
    setStats({
      customers: customers?.length || 0,
      deliveries: deliveries?.length || 0,
      payments: totalPayments.length || 0,
      totalPending: totalAmount - totalPaid,
      totalCollected: totalPaid
    });
  }, [customers, deliveries, payments]);

  // Show notification for high priority reminders
  useEffect(() => {
    if (reminders && reminders.length > 0) {
      const highPriority = reminders.filter(r => r.priority === 'high');
      if (highPriority.length > 0) {
        toast.error(
          `⚠️ ${highPriority.length} high priority reminder${highPriority.length > 1 ? 's' : ''} need immediate attention!`,
          { duration: 5000 }
        );
      } else if (reminders.length > 0) {
        toast.success(
          `📋 ${reminders.length} reminder${reminders.length > 1 ? 's' : ''} pending`,
          { duration: 3000 }
        );
      }
    }
  }, [reminders]);

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'high': return 'bg-red-100 border-red-500 text-red-800';
      case 'medium': return 'bg-yellow-100 border-yellow-500 text-yellow-800';
      case 'low': return 'bg-green-100 border-green-500 text-green-800';
      default: return 'bg-gray-100 border-gray-500 text-gray-800';
    }
  };

  const getPriorityIcon = (priority) => {
    switch(priority) {
      case 'high': return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'medium': return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'low': return <CheckCircle className="w-4 h-4 text-green-500" />;
      default: return <Bell className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'pending': return 'Payment Pending';
      case 'delivery_pending': return 'Delivery & Payment Pending';
      case 'delivery_collection': return 'Cylinder Collection Due';
      default: return 'Unknown';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navbar */}
      <nav className="bg-gradient-to-r from-blue-700 to-blue-800 text-white p-4 shadow-lg sticky top-0 z-50">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">🏪 Customer Management System</h1>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center gap-2 bg-blue-600 px-3 py-1 rounded-full">
              <User className="w-4 h-4" />
              <span className="text-sm font-medium">{user?.username || 'Admin'}</span>
            </div>
            <button
              onClick={() => navigate('/reminders')}
              className="relative bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-2 rounded-md transition flex items-center gap-2"
            >
              <Bell size={18} />
              {reminders?.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                  {reminders.length}
                </span>
              )}
              <span className="hidden sm:inline">Reminders</span>
            </button>
            <button
              onClick={logout}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md transition flex items-center gap-2"
            >
              <LogOut size={18} />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto p-4 md:p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8">
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 border-b-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-xs md:text-sm">Total Customers</p>
                <p className="text-2xl md:text-3xl font-bold text-blue-700">{stats.customers}</p>
              </div>
              <Users className="w-10 h-10 md:w-12 md:h-12 text-blue-500 opacity-50" />
            </div>
          </div>
          
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 border-b-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-xs md:text-sm">Total Deliveries</p>
                <p className="text-2xl md:text-3xl font-bold text-purple-700">{stats.deliveries}</p>
              </div>
              <Package className="w-10 h-10 md:w-12 md:h-12 text-purple-500 opacity-50" />
            </div>
          </div>
          
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 border-b-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-xs md:text-sm">Total Payments</p>
                <p className="text-2xl md:text-3xl font-bold text-green-700">{stats.payments}</p>
              </div>
              <DollarSign className="w-10 h-10 md:w-12 md:h-12 text-green-500 opacity-50" />
            </div>
          </div>
          
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 border-b-4 border-red-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-xs md:text-sm">Pending Amount</p>
                <p className="text-2xl md:text-3xl font-bold text-red-700">₹{stats.totalPending.toFixed(0)}</p>
              </div>
              <TrendingUp className="w-10 h-10 md:w-12 md:h-12 text-red-500 opacity-50" />
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6 mb-8">
          <button
            onClick={() => navigate('/customers')}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold p-3 md:p-4 rounded-lg transition shadow-md hover:shadow-lg flex items-center justify-center gap-2"
          >
            <Users size={20} />
            <span className="text-sm md:text-base">Customers</span>
          </button>

          <button
            onClick={() => navigate('/deliveries')}
            className="bg-purple-600 hover:bg-purple-700 text-white font-semibold p-3 md:p-4 rounded-lg transition shadow-md hover:shadow-lg flex items-center justify-center gap-2"
          >
            <Package size={20} />
            <span className="text-sm md:text-base">Deliveries</span>
          </button>

          <button
            onClick={() => navigate('/payments')}
            className="bg-green-600 hover:bg-green-700 text-white font-semibold p-3 md:p-4 rounded-lg transition shadow-md hover:shadow-lg flex items-center justify-center gap-2"
          >
            <DollarSign size={20} />
            <span className="text-sm md:text-base">Payments</span>
          </button>

          <button
            onClick={() => navigate('/reminders')}
            className="relative bg-yellow-600 hover:bg-yellow-700 text-white font-semibold p-3 md:p-4 rounded-lg transition shadow-md hover:shadow-lg flex items-center justify-center gap-2"
          >
            <Bell size={20} />
            <span className="text-sm md:text-base">Reminders</span>
            {reminders?.length > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                {reminders.length}
              </span>
            )}
          </button>

          <button
            onClick={() => navigate('/admins')}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold p-3 md:p-4 rounded-lg transition shadow-md hover:shadow-lg flex items-center justify-center gap-2"
          >
            <UserCog size={20} />
            <span className="text-sm md:text-base">Admins</span>
          </button>
        </div>

        {/* Reminders Section */}
        {reminders && reminders.length > 0 && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 p-4">
              <h2 className="text-xl font-bold text-white flex items-center">
                <Bell className="mr-2" />
                Reminders ({reminders.length})
                <span className="ml-3 text-sm font-normal bg-yellow-700 px-3 py-1 rounded-full">
                  {reminders.filter(r => r.priority === 'high').length} High Priority
                </span>
              </h2>
            </div>
            
            <div className="p-4 max-h-96 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {reminders.slice(0, 6).map((reminder, index) => (
                  <div 
                    key={index} 
                    className={`border-l-4 p-4 rounded-lg shadow-sm hover:shadow-md transition ${getPriorityColor(reminder.priority)}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          {getPriorityIcon(reminder.priority)}
                          <h3 className="font-semibold text-gray-800">{reminder.customer_name}</h3>
                        </div>
                        
                        <div className="mt-2 space-y-1">
                          {reminder.contact_number && (
                            <p className="text-sm text-gray-600 flex items-center">
                              <Phone className="w-3 h-3 mr-1" />
                              {reminder.contact_number}
                            </p>
                          )}
                          
                          {reminder.remaining_amount > 0 && (
                            <p className="text-sm font-medium text-red-600">
                              Pending: ₹{reminder.remaining_amount.toFixed(2)}
                            </p>
                          )}
                          
                          {reminder.days_since_payment > 0 && (
                            <p className="text-sm text-gray-600 flex items-center">
                              <Calendar className="w-3 h-3 mr-1" />
                              {reminder.days_since_payment} days since last payment
                            </p>
                          )}
                          
                          {reminder.days_since_delivery > 0 && (
                            <p className="text-sm text-red-600 flex items-center">
                              <Clock className="w-3 h-3 mr-1" />
                              {reminder.days_since_delivery} days overdue
                            </p>
                          )}
                        </div>
                        
                        <div className="mt-2">
                          <span className="text-xs px-2 py-1 bg-gray-200 rounded-full">
                            {getStatusText(reminder.pending_status)}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex flex-col gap-2 ml-2">
                        <button
                          onClick={() => navigate('/payments')}
                          className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded transition"
                        >
                          Pay
                        </button>
                        <button
                          onClick={() => navigate('/customers')}
                          className="text-xs bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded transition"
                        >
                          View
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {reminders.length > 6 && (
                <div className="text-center mt-4">
                  <button
                    onClick={() => navigate('/reminders')}
                    className="text-blue-600 hover:text-blue-800 font-semibold flex items-center justify-center gap-1"
                  >
                    View All {reminders.length} Reminders
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* No Reminders Message */}
        {reminders && reminders.length === 0 && (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">All Clear! 🎉</h2>
            <p className="text-gray-600">No pending payments or overdue deliveries.</p>
            <p className="text-gray-500 text-sm mt-2">Everything is up to date!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;